import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { finance } from '@/lib/api';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, ArrowRightLeft, Plus,
  CreditCard, Landmark, Wallet, X, Check, Ban, FileText, RefreshCw, Edit2, Trash2, Building, ChevronDown, ChevronUp, Calendar, Search, Eye, Filter
} from 'lucide-react';

const TABS = [
  { id: 'dashboard', label: 'Resumo' },
  { id: 'payables', label: 'A Pagar' },
  { id: 'receivables', label: 'A Receber' },
  { id: 'accounts', label: 'Contas' },
  { id: 'transactions', label: 'Extrato' },
  { id: 'reconciliacao', label: 'Conciliação' },
  { id: 'dre', label: 'DRE' },
];

const PAYABLE_CATEGORIES = [
  'INSUMOS', 'ALUGUEL', 'ENERGIA', 'AGUA', 'INTERNET', 'FOLHA',
  'IMPOSTOS', 'TAXAS_CANAL', 'ENTREGA', 'EMBALAGEM', 'COMISSOES',
  'JUROS', 'TARIFAS', 'FIXO', 'OUTROS'
];

const RECEIVABLE_CATEGORIES = ['VENDAS', 'SERVICOS', 'OUTROS'];

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('pt-BR');
}

function StatusBadge({ status }) {
  const colors = {
    PENDENTE: 'bg-orange-100 text-orange-700',
    PAGO: 'bg-emerald-100 text-emerald-700',
    PARCIAL: 'bg-blue-100 text-blue-700',
    CANCELADO: 'bg-slate-100 text-slate-500',
    ATRASADO: 'bg-red-100 text-red-700',
  };
  return (
    <span data-testid={`status-badge-${status}`} className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

// ============ DASHBOARD TAB ============
function DashboardTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    finance.summary().then(r => setSummary(r.data)).catch(() => toast.error('Erro ao carregar resumo')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-500">Carregando...</div>;
  if (!summary) return null;

  const cards = [
    { label: 'A Pagar (Hoje)', value: summary.payables_today, icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'A Pagar (7d)', value: summary.payables_7d, icon: TrendingDown, color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'A Pagar (30d)', value: summary.payables_30d, icon: TrendingDown, color: 'text-red-400', bg: 'bg-red-50' },
    { label: 'Vencidas', value: summary.payables_overdue, icon: Ban, color: 'text-red-700', bg: 'bg-red-100' },
    { label: 'A Receber (Hoje)', value: summary.receivables_today, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'A Receber (7d)', value: summary.receivables_7d, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'A Receber (30d)', value: summary.receivables_30d, icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-50' },
    { label: 'Vencidos', value: summary.receivables_overdue, icon: Ban, color: 'text-orange-700', bg: 'bg-orange-100' },
  ];

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {/* Account balances */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Saldos das Contas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {summary.accounts.map(acc => (
            <div key={acc.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`account-card-${acc.id}`}>
              <div className="flex items-center gap-3 mb-2">
                {acc.type === 'CAIXA' ? <Wallet size={20} className="text-emerald-600" /> :
                 acc.type === 'BANCO' ? <Landmark size={20} className="text-blue-600" /> :
                 <CreditCard size={20} className="text-purple-600" />}
                <span className="font-medium text-slate-700">{acc.name}</span>
              </div>
              <p className={`text-2xl font-bold ${acc.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(acc.balance)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className={`${c.bg} rounded-xl p-4 border border-slate-100`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={c.color} />
                <span className="text-xs font-medium text-slate-600">{c.label}</span>
              </div>
              <p className={`text-lg font-bold ${c.color}`}>{formatCurrency(c.value)}</p>
            </div>
          );
        })}
      </div>

      {/* CMV & Margin */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-500 mb-1">CMV (30 dias)</h4>
          <p className="text-2xl font-bold text-slate-800">{formatCurrency(summary.cmv_period)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-500 mb-1">Margem Estimada (30 dias)</h4>
          <p className="text-2xl font-bold text-emerald-600">{summary.estimated_margin}%</p>
        </div>
      </div>
    </div>
  );
}

// ============ PAYABLES TAB ============
function PayablesTab() {
  const [payables, setPayables] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('PIX');
  const [payAccountId, setPayAccountId] = useState('');
  const [form, setForm] = useState({ supplier_name: '', description: '', category: 'INSUMOS', amount: '', due_date: '', account_id: '' });
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    finance.payables.list(filter ? { status: filter } : {}).then(r => setPayables(r.data));
    finance.accounts.list().then(r => setAccounts(r.data));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await finance.payables.create({ ...form, amount: parseFloat(form.amount), due_date: new Date(form.due_date).toISOString(), account_id: form.account_id || null });
      toast.success('Conta a pagar criada');
      setShowForm(false);
      setForm({ supplier_name: '', description: '', category: 'INSUMOS', amount: '', due_date: '', account_id: '' });
      load();
    } catch { toast.error('Erro ao criar'); }
  };

  const handlePay = async (id) => {
    try {
      await finance.payables.pay(id, { amount: parseFloat(payAmount), payment_method: payMethod, account_id: payAccountId || null });
      toast.success('Pagamento registrado');
      setPayingId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar esta conta?')) return;
    try { await finance.payables.cancel(id); toast.success('Cancelada'); load(); } catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4" data-testid="payables-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {['', 'PENDENTE', 'PARCIAL', 'PAGO', 'CANCELADO'].map(s => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`payable-filter-${s || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === s ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
              {s || 'Todos'}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(true)} data-testid="create-payable-btn"
          className="flex items-center gap-2 bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#e55a5a] transition-colors">
          <Plus size={16} /> Nova Conta a Pagar
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="payable-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input placeholder="Fornecedor" value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="payable-supplier" />
            <input placeholder="Descricao *" required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="payable-description" />
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="payable-category">
              {PAYABLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="Valor *" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="payable-amount" />
            <input type="date" required value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="payable-due-date" />
            <select value={form.account_id} onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="payable-account">
              <option value="">Conta (opcional)</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="payable-submit" className="bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-sm">Cancelar</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Fornecedor</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Descricao</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Categoria</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Valor</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Pago</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Vencimento</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {payables.map(p => (
              <React.Fragment key={p.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payable-row-${p.id}`}>
                  <td className="px-4 py-3 text-slate-700">{p.supplier_name || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{p.description}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{p.category || '-'}</span></td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(p.amount)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(p.paid_amount)}</td>
                  <td className="px-4 py-3">{formatDate(p.due_date)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {!['PAGO', 'CANCELADO'].includes(p.status) && (
                        <>
                          <button onClick={() => { setPayingId(payingId === p.id ? null : p.id); setPayAmount(String(p.amount - p.paid_amount)); }}
                            data-testid={`pay-payable-${p.id}`} className="p-1.5 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100" title="Pagar">
                            <Check size={14} />
                          </button>
                          <button onClick={() => handleCancel(p.id)} data-testid={`cancel-payable-${p.id}`}
                            className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100" title="Cancelar">
                            <X size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {payingId === p.id && (
                  <tr className="bg-emerald-50" data-testid={`pay-form-${p.id}`}>
                    <td colSpan={8} className="px-4 py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm w-32" placeholder="Valor" data-testid="pay-amount-input" />
                        <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm" data-testid="pay-method-select">
                          <option>PIX</option><option>DINHEIRO</option><option>CARTAO</option><option>BOLETO</option>
                        </select>
                        <select value={payAccountId} onChange={e => setPayAccountId(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm" data-testid="pay-account-select">
                          <option value="">Conta</option>
                          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        <button onClick={() => handlePay(p.id)} data-testid="confirm-pay-btn"
                          className="bg-emerald-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium">Confirmar Pagamento</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {payables.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Nenhuma conta a pagar encontrada</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ RECEIVABLES TAB ============
function ReceivablesTab() {
  const [receivables, setReceivables] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [receivingId, setReceivingId] = useState(null);
  const [recvAmount, setRecvAmount] = useState('');
  const [recvMethod, setRecvMethod] = useState('PIX');
  const [recvAccountId, setRecvAccountId] = useState('');
  const [form, setForm] = useState({ customer_name: '', description: '', category: 'VENDAS', amount: '', due_date: '', account_id: '' });
  const [filter, setFilter] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  const load = useCallback(() => {
    const params = {};
    if (filter) params.status = filter;
    if (dateFilter.from) params.from_date = dateFilter.from;
    if (dateFilter.to) params.to_date = dateFilter.to;
    finance.receivables.list(params).then(r => setReceivables(r.data));
    finance.accounts.list().then(r => setAccounts(r.data));
  }, [filter, dateFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await finance.receivables.create({ ...form, amount: parseFloat(form.amount), due_date: new Date(form.due_date).toISOString(), account_id: form.account_id || null });
      toast.success('Conta a receber criada');
      setShowForm(false);
      setForm({ customer_name: '', description: '', category: 'VENDAS', amount: '', due_date: '', account_id: '' });
      load();
    } catch { toast.error('Erro ao criar'); }
  };

  const handleReceive = async (id) => {
    try {
      await finance.receivables.receive(id, { amount: parseFloat(recvAmount), payment_method: recvMethod, account_id: recvAccountId || null });
      toast.success('Recebimento registrado');
      setReceivingId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar esta conta?')) return;
    try { await finance.receivables.cancel(id); toast.success('Cancelada'); load(); } catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4" data-testid="receivables-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap items-center">
          {['', 'PENDENTE', 'PARCIAL', 'PAGO', 'CANCELADO'].map(s => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`receivable-filter-${s || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === s ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
              {s || 'Todos'}
            </button>
          ))}
          <span className="text-slate-400 mx-2">|</span>
          <input type="date" value={dateFilter.from} onChange={e => setDateFilter(d => ({ ...d, from: e.target.value }))}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="De" data-testid="receivable-date-from" />
          <input type="date" value={dateFilter.to} onChange={e => setDateFilter(d => ({ ...d, to: e.target.value }))}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="Até" data-testid="receivable-date-to" />
        </div>
        <button onClick={() => setShowForm(true)} data-testid="create-receivable-btn"
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors">
          <Plus size={16} /> Nova Conta a Receber
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="receivable-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input placeholder="Cliente" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="receivable-customer" />
            <input placeholder="Descricao *" required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="receivable-description" />
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="receivable-category">
              {RECEIVABLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="Valor *" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="receivable-amount" />
            <input type="date" required value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="receivable-due-date" />
            <select value={form.account_id} onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="receivable-account">
              <option value="">Conta (opcional)</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="receivable-submit" className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-sm">Cancelar</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Descricao</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Canal</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Valor</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Recebido</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Vencimento</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {receivables.map(r => (
              <React.Fragment key={r.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`receivable-row-${r.id}`}>
                  <td className="px-4 py-3 text-slate-700">{r.customer_name || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{r.description}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    r.channel === 'PDV' ? 'bg-blue-100 text-blue-700' :
                    r.channel === 'DELIVERY' ? 'bg-orange-100 text-orange-700' :
                    r.channel === 'CARDAPIO_DIGITAL' ? 'bg-purple-100 text-purple-700' :
                    r.channel === 'IFOOD' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{r.channel || r.category || '-'}</span></td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(r.amount)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(r.received_amount)}</td>
                  <td className="px-4 py-3">{formatDate(r.due_date)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {!['PAGO', 'CANCELADO'].includes(r.status) && (
                        <>
                          <button onClick={() => { setReceivingId(receivingId === r.id ? null : r.id); setRecvAmount(String(r.amount - r.received_amount)); }}
                            data-testid={`receive-receivable-${r.id}`} className="p-1.5 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100" title="Receber">
                            <Check size={14} />
                          </button>
                          <button onClick={() => handleCancel(r.id)} data-testid={`cancel-receivable-${r.id}`}
                            className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100" title="Cancelar">
                            <X size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {receivingId === r.id && (
                  <tr className="bg-emerald-50" data-testid={`receive-form-${r.id}`}>
                    <td colSpan={8} className="px-4 py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="number" step="0.01" value={recvAmount} onChange={e => setRecvAmount(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm w-32" data-testid="receive-amount-input" />
                        <select value={recvMethod} onChange={e => setRecvMethod(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm" data-testid="receive-method-select">
                          <option>PIX</option><option>DINHEIRO</option><option>CARTAO</option>
                        </select>
                        <select value={recvAccountId} onChange={e => setRecvAccountId(e.target.value)}
                          className="px-3 py-1.5 border rounded-lg text-sm" data-testid="receive-account-select">
                          <option value="">Conta</option>
                          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        <button onClick={() => handleReceive(r.id)} data-testid="confirm-receive-btn"
                          className="bg-emerald-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium">Confirmar Recebimento</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {receivables.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Nenhuma conta a receber encontrada</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ ACCOUNTS TAB ============
function AccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form, setForm] = useState({ name: '', account_type: 'BANCO', bank_name: '', agency: '', account_number: '', initial_balance: '0' });
  const [editForm, setEditForm] = useState({ name: '', account_type: '', bank_name: '', agency: '', account_number: '', is_active: true });
  const [transfer, setTransfer] = useState({ from_account_id: '', to_account_id: '', amount: '', description: '' });

  const load = useCallback(() => { finance.accounts.list().then(r => setAccounts(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await finance.accounts.create({ ...form, initial_balance: parseFloat(form.initial_balance || '0') });
      toast.success('Conta criada');
      setShowForm(false);
      setForm({ name: '', account_type: 'BANCO', bank_name: '', agency: '', account_number: '', initial_balance: '0' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar conta'); }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await finance.transfer({ ...transfer, amount: parseFloat(transfer.amount) });
      toast.success('Transferencia realizada');
      setShowTransfer(false);
      setTransfer({ from_account_id: '', to_account_id: '', amount: '', description: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const openEdit = (acc) => {
    setEditingAccount(acc);
    setEditForm({ name: acc.name, account_type: acc.account_type, bank_name: acc.bank_name || '', agency: acc.agency || '', account_number: acc.account_number || '', is_active: acc.is_active !== false });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await finance.accounts.update(editingAccount.id, editForm);
      toast.success('Conta atualizada');
      setEditingAccount(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atualizar'); }
  };

  const handleDelete = async (acc) => {
    const msg = 'Deseja excluir a conta "' + acc.name + '"?\n\nSe houver lancamentos vinculados, a conta sera apenas desativada.';
    if (!window.confirm(msg)) return;
    try {
      const res = await finance.accounts.delete(acc.id);
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const activeAccounts = accounts.filter(a => a.is_active !== false);
  const inactiveAccounts = accounts.filter(a => a.is_active === false);

  return (
    <div className="space-y-4" data-testid="accounts-tab">
      <div className="flex gap-2 justify-end flex-wrap">
        <button onClick={async () => {
          try { const res = await finance.seedAccounts(); toast.success(res.data.message); load(); } catch { toast.error('Erro ao criar contas padrao'); }
        }} data-testid="seed-accounts-btn" className="flex items-center gap-2 bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700">
          <RefreshCw size={16} /> Criar Contas Padrao
        </button>
        <button onClick={async () => {
          try { toast.info('Sincronizando...'); const res = await finance.ledger.syncOrders(30); toast.success(res.data.synced + ' pedidos sincronizados'); } catch { toast.error('Erro ao sincronizar'); }
        }} data-testid="sync-ledger-btn" className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700">
          <RefreshCw size={16} /> Sincronizar Ledger
        </button>
        <button onClick={() => setShowTransfer(!showTransfer)} data-testid="transfer-btn" className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <ArrowRightLeft size={16} /> Transferir
        </button>
        <button onClick={() => setShowForm(!showForm)} data-testid="create-account-btn" className="flex items-center gap-2 bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#e55a5a]">
          <Plus size={16} /> Nova Conta
        </button>
      </div>

      {showTransfer && (
        <form onSubmit={handleTransfer} className="bg-blue-50 rounded-xl border border-blue-200 p-5 space-y-4" data-testid="transfer-form">
          <h4 className="font-semibold text-blue-800">Transferencia entre Contas</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select required value={transfer.from_account_id} onChange={e => setTransfer(t => ({ ...t, from_account_id: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" data-testid="transfer-from">
              <option value="">Origem *</option>
              {activeAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({formatCurrency(a.current_balance)})</option>)}
            </select>
            <select required value={transfer.to_account_id} onChange={e => setTransfer(t => ({ ...t, to_account_id: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" data-testid="transfer-to">
              <option value="">Destino *</option>
              {activeAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input type="number" step="0.01" required placeholder="Valor *" value={transfer.amount} onChange={e => setTransfer(t => ({ ...t, amount: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" data-testid="transfer-amount" />
            <input placeholder="Descricao" value={transfer.description} onChange={e => setTransfer(t => ({ ...t, description: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" data-testid="transfer-description" />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="transfer-submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Confirmar</button>
            <button type="button" onClick={() => setShowTransfer(false)} className="bg-slate-100 px-4 py-2 rounded-lg text-sm">Cancelar</button>
          </div>
        </form>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="account-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input placeholder="Nome da Conta *" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="account-name" />
            <select value={form.account_type} onChange={e => setForm(f => ({ ...f, account_type: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="account-type">
              <option value="BANCO">Banco</option><option value="CAIXA">Caixa</option><option value="CARTAO">Cartao</option><option value="MARKETPLACE">Marketplace</option>
            </select>
            <input placeholder="Banco" value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="account-bank" />
            <input placeholder="Agencia" value={form.agency} onChange={e => setForm(f => ({ ...f, agency: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="account-agency" />
            <input placeholder="Numero da Conta" value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="account-number" />
            <input type="number" step="0.01" placeholder="Saldo Inicial" value={form.initial_balance} onChange={e => setForm(f => ({ ...f, initial_balance: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="account-balance" />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="account-submit" className="bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-sm">Cancelar</button>
          </div>
        </form>
      )}

      {editingAccount && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setEditingAccount(null)}>
          <form onSubmit={handleEdit} onClick={e => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4 shadow-2xl" data-testid="edit-account-modal">
            <h4 className="font-semibold text-slate-800 text-lg">Editar Conta</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input placeholder="Nome *" required value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-account-name" />
              <select value={editForm.account_type} onChange={e => setEditForm(f => ({ ...f, account_type: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-account-type">
                <option value="BANCO">Banco</option><option value="CAIXA">Caixa</option><option value="CARTAO">Cartao</option><option value="MARKETPLACE">Marketplace</option>
              </select>
              <input placeholder="Banco" value={editForm.bank_name} onChange={e => setEditForm(f => ({ ...f, bank_name: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-account-bank" />
              <input placeholder="Agencia" value={editForm.agency} onChange={e => setEditForm(f => ({ ...f, agency: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-account-agency" />
              <input placeholder="Numero da Conta" value={editForm.account_number} onChange={e => setEditForm(f => ({ ...f, account_number: e.target.value }))} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-account-number" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} data-testid="edit-account-active" />
                Conta Ativa
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setEditingAccount(null)} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-sm">Cancelar</button>
              <button type="submit" data-testid="edit-account-submit" className="bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium">Salvar Alteracoes</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {activeAccounts.map(acc => (
          <div key={acc.id} className="bg-white rounded-xl border border-slate-200 p-5 relative group" data-testid={'account-card-' + acc.id}>
            <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => openEdit(acc)} data-testid={'edit-account-' + acc.id} title="Editar" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600"><Edit2 size={14} /></button>
              <button onClick={() => handleDelete(acc)} data-testid={'delete-account-' + acc.id} title="Excluir" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              {acc.account_type === 'CAIXA' ? <Wallet size={24} className="text-emerald-600" /> :
               acc.account_type === 'BANCO' ? <Landmark size={24} className="text-blue-600" /> :
               acc.account_type === 'MARKETPLACE' ? <Building size={24} className="text-amber-600" /> :
               <CreditCard size={24} className="text-purple-600" />}
              <div>
                <h4 className="font-semibold text-slate-800">{acc.name}</h4>
                <p className="text-xs text-slate-500">{acc.account_type} {acc.bank_name ? '- ' + acc.bank_name : ''}</p>
              </div>
            </div>
            <p className={'text-2xl font-bold ' + (acc.current_balance >= 0 ? 'text-emerald-600' : 'text-red-600')}>
              {formatCurrency(acc.current_balance)}
            </p>
            {acc.agency && <p className="text-xs text-slate-400 mt-2">Ag: {acc.agency} | CC: {acc.account_number}</p>}
          </div>
        ))}
        {activeAccounts.length === 0 && <p className="text-slate-400 col-span-3 text-center py-8">Nenhuma conta ativa cadastrada</p>}
      </div>

      {inactiveAccounts.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-slate-500 mb-3">Contas Inativas ({inactiveAccounts.length})</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {inactiveAccounts.map(acc => (
              <div key={acc.id} className="bg-slate-50 rounded-xl border border-slate-200 p-4 opacity-60 relative group" data-testid={'account-inactive-' + acc.id}>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(acc)} title="Editar/Reativar" className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-blue-600"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(acc)} title="Excluir" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-500">{acc.name}</span>
                  <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded">INATIVA</span>
                </div>
                <p className="text-lg font-bold text-slate-400 mt-1">{formatCurrency(acc.current_balance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function TransactionsTab() {
  const [extrato, setExtrato] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({ account_id: '' });
  const [displayMode, setDisplayMode] = useState('LIQUIDO'); // BRUTO | TAXA | LIQUIDO
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.account_id) params.account_id = filters.account_id;
      // Convert date to ISO format for backend
      if (dateRange.start) {
        params.start = new Date(dateRange.start + 'T00:00:00').toISOString();
      }
      if (dateRange.end) {
        params.end = new Date(dateRange.end + 'T23:59:59').toISOString();
      }
      
      const [extratoRes, accountsRes] = await Promise.all([
        finance.ledger.extrato(params),
        finance.accounts.list()
      ]);
      setExtrato(extratoRes.data);
      setAccounts(accountsRes.data);
    } catch (err) {
      toast.error('Erro ao carregar extrato');
    } finally {
      setLoading(false);
    }
  }, [filters, dateRange]);

  useEffect(() => { load(); }, [load]);

  // Sincronizar taxas de todos os pagamentos E corrigir contas
  const handleSyncAllFees = async () => {
    if (!window.confirm('Isso irá:\n1. Corrigir as contas (Cartão → Maquininha Mercado Pago)\n2. Sincronizar taxas de TODOS os pagamentos (90 dias)\n\nContinuar?')) return;
    setSyncing(true);
    try {
      // Primeiro corrige as contas
      const fixRes = await finance.ledger.fixAccounts();
      const fixData = fixRes.data;
      
      // Depois sincroniza as taxas
      const syncRes = await finance.ledger.syncAllFees(90);
      const syncData = syncRes.data;
      
      toast.success(`Correção concluída!\n${fixData.fixed || 0} contas corrigidas\n${syncData.fee_entries_created || 0} taxas aplicadas`);
      load(); // Recarregar extrato
    } catch (err) {
      toast.error('Erro ao sincronizar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const getDisplayValue = (row) => {
    if (displayMode === 'BRUTO') return row.grossAmount;
    if (displayMode === 'TAXA') return row.feeAmount;
    return row.netAmount;
  };

  const getDisplayLabel = () => {
    if (displayMode === 'BRUTO') return 'Bruto';
    if (displayMode === 'TAXA') return 'Taxa';
    return 'Liquido';
  };

  return (
    <div className="space-y-4" data-testid="transactions-tab">
      {/* Summary cards */}
      {extrato?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Saldo Inicial</p>
            <p className="text-lg font-bold text-slate-700">{formatCurrency(extrato.summary.openingBalance)}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
            <p className="text-xs text-emerald-600">Entradas (Bruto)</p>
            <p className="text-lg font-bold text-emerald-700">+{formatCurrency(extrato.summary.totalInGross)}</p>
          </div>
          <div className="bg-orange-50 rounded-xl border border-orange-100 p-4">
            <p className="text-xs text-orange-600">Taxas do Periodo</p>
            <p className="text-lg font-bold text-orange-700">-{formatCurrency(extrato.summary.totalFees)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Saldo (Bruto)</p>
            <p className={`text-lg font-bold ${extrato.summary.closingBalanceGross >= 0 ? 'text-slate-700' : 'text-red-700'}`}>
              {formatCurrency(extrato.summary.closingBalanceGross)}
            </p>
          </div>
          <div className="bg-emerald-100 rounded-xl border border-emerald-200 p-4">
            <p className="text-xs text-emerald-700">Saldo (Liquido)</p>
            <p className={`text-lg font-bold ${extrato.summary.closingBalanceNet >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>
              {formatCurrency(extrato.summary.closingBalanceNet)}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <select value={filters.account_id} onChange={e => setFilters(f => ({ ...f, account_id: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tx-filter-account">
          <option value="">Todas as Contas</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Exibir:</span>
          {['BRUTO', 'TAXA', 'LIQUIDO'].map(mode => (
            <button key={mode} onClick={() => setDisplayMode(mode)} data-testid={`display-mode-${mode}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                displayMode === mode ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}>
              {mode === 'BRUTO' ? 'Bruto' : mode === 'TAXA' ? 'Taxa' : 'Liquido'}
            </button>
          ))}
        </div>
        
        <input type="date" value={dateRange.start} onChange={e => setDateRange(d => ({ ...d, start: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tx-date-start" />
        <input type="date" value={dateRange.end} onChange={e => setDateRange(d => ({ ...d, end: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tx-date-end" />
        
        <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="tx-refresh">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
        
        {/* Botão de Sincronização de Taxas */}
        <button 
          onClick={handleSyncAllFees} 
          disabled={syncing}
          className="ml-auto px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
          data-testid="sync-fees-btn"
          title="Sincronizar taxas de cartão e PIX para todos os pagamentos"
        >
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Sincronizando...' : 'Sincronizar Taxas'}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Data</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Conta</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Descricao</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Canal</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Metodo</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600" title="Bruto / Taxa / Liquido">
                Valor ({getDisplayLabel()})
              </th>
            </tr>
          </thead>
          <tbody>
            {extrato?.rows?.map(row => (
              <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${row.id}`}>
                <td className="px-4 py-3 text-slate-500">{formatDate(row.date)}</td>
                <td className="px-4 py-3 text-slate-600">{row.accountName || '-'}</td>
                <td className="px-4 py-3 text-slate-700">{row.description || '-'}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{row.channel || '-'}</span>
                </td>
                <td className="px-4 py-3 text-slate-500">{row.method || '-'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="relative group">
                    <span className={`font-medium ${getDisplayValue(row) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {getDisplayValue(row) >= 0 ? '+' : ''}{formatCurrency(getDisplayValue(row))}
                    </span>
                    {/* Tooltip com detalhes */}
                    <div className="absolute right-0 top-full mt-1 bg-slate-800 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap pointer-events-none">
                      <div className="space-y-1">
                        <div className="flex justify-between gap-4">
                          <span>Bruto:</span>
                          <span className="font-medium">{formatCurrency(row.grossAmount)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Taxa:</span>
                          <span className="font-medium text-orange-400">{formatCurrency(row.feeAmount)}</span>
                        </div>
                        <div className="flex justify-between gap-4 border-t border-slate-600 pt-1">
                          <span>Liquido:</span>
                          <span className="font-medium text-emerald-400">{formatCurrency(row.netAmount)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {(!extrato?.rows || extrato.rows.length === 0) && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                {loading ? 'Carregando...' : 'Nenhum lancamento encontrado no ledger'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ RECONCILIACAO DIARIA TAB (Fonte: v_daily_financial_reconciliation) ============
function ReconciliacaoTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await finance.reconciliation.daily(date);
      setData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar conciliação: ' + (err.response?.data?.detail || err.message));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const channelColors = {
    PDV: 'bg-slate-100 text-slate-700',
    CARDAPIO_DIGITAL: 'bg-blue-100 text-blue-700',
    WHATSAPP: 'bg-emerald-100 text-emerald-700',
    IFOOD: 'bg-red-100 text-red-700',
    KEETA: 'bg-purple-100 text-purple-700',
    '99FOOD': 'bg-amber-100 text-amber-700',
    LOJA: 'bg-slate-100 text-slate-700',
  };

  return (
    <div className="space-y-4" data-testid="reconciliacao-tab">
      {/* Header + date picker */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Dia:</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="reconc-date" />
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
            data-testid="reconc-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        {data?.has_estimated_fee && (
          <div className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="reconc-estimated-badge">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            Há canais com taxa estimada (Keeta fallback 10%)
          </div>
        )}
      </div>

      {/* Totals cards */}
      {data?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="reconc-totals">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Bruto (Vendas)</p>
            <p className="text-lg font-bold text-slate-800">{formatCurrency(data.totals.bruto)}</p>
          </div>
          <div className="bg-orange-50 rounded-xl border border-orange-100 p-4">
            <p className="text-xs text-orange-600">Frete Próprio (custo)</p>
            <p className="text-lg font-bold text-orange-700">-{formatCurrency(data.totals.delivery_cost_real)}</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-100 p-4">
            <p className="text-xs text-red-600">Taxas Marketplace</p>
            <p className="text-lg font-bold text-red-700">-{formatCurrency(data.totals.marketplace_fees)}</p>
          </div>
          <div className="bg-purple-50 rounded-xl border border-purple-100 p-4">
            <p className="text-xs text-purple-600">Taxas de Cartão</p>
            <p className="text-lg font-bold text-purple-700">-{formatCurrency(data.totals.card_fees)}</p>
          </div>
          <div className="bg-emerald-100 rounded-xl border border-emerald-200 p-4">
            <p className="text-xs text-emerald-700">Receita Líquida</p>
            <p className="text-xl font-bold text-emerald-800">{formatCurrency(data.totals.receita_liquida)}</p>
          </div>
        </div>
      )}

      {/* Per-channel breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-800">Conciliação por Canal</h3>
          <p className="text-xs text-slate-500">Sem dupla dedução: frete de marketplaces é apenas informativo.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Canal</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Pedidos</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Bruto</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600" title="Frete cobrado do cliente (informativo)">Frete (info)</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600" title="Frete pago pela loja (só canais próprios)">Custo Frete</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Taxa Marketplace</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Taxa Cartão</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Receita Líquida</th>
              </tr>
            </thead>
            <tbody>
              {data?.channels?.map(ch => (
                <tr key={ch.canal} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`reconc-row-${ch.canal}`}>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${channelColors[ch.canal] || 'bg-slate-100 text-slate-600'}`}>
                      {ch.canal}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{ch.pedidos}</td>
                  <td className="px-4 py-3 text-right font-mono font-medium text-slate-800">{formatCurrency(ch.bruto)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-400" title="Informativo: cobrado do cliente">{formatCurrency(ch.delivery_fee_informativo)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${ch.delivery_cost_real > 0 ? 'text-orange-600' : 'text-slate-300'}`}>
                    {ch.delivery_cost_real > 0 ? '-' + formatCurrency(ch.delivery_cost_real) : '—'}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${ch.marketplace_fee > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                    {ch.marketplace_fee > 0 ? (
                      <span>
                        -{formatCurrency(ch.marketplace_fee)}
                        <span className="ml-1 text-xs text-slate-400">({ch.marketplace_fee_pct}%)</span>
                        {ch.marketplace_fee_estimated && (
                          <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium" data-testid={`estimated-flag-${ch.canal}`}>est.</span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${ch.card_fee > 0 ? 'text-purple-600' : 'text-slate-300'}`}>
                    {ch.card_fee > 0 ? '-' + formatCurrency(ch.card_fee) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{formatCurrency(ch.receita_liquida)}</td>
                </tr>
              ))}
              {(!data?.channels || data.channels.length === 0) && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                  {loading ? 'Carregando...' : 'Nenhuma movimentação neste dia'}
                </td></tr>
              )}
            </tbody>
            {data?.channels && data.channels.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr data-testid="reconc-totals-row">
                  <td className="px-4 py-3 font-bold text-slate-800">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-800">
                    {data.channels.reduce((s, c) => s + (c.pedidos || 0), 0)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">{formatCurrency(data.totals.bruto)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-400">
                    {formatCurrency(data.channels.reduce((s, c) => s + (c.delivery_fee_informativo || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-orange-700">-{formatCurrency(data.totals.delivery_cost_real)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-red-700">-{formatCurrency(data.totals.marketplace_fees)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-purple-700">-{formatCurrency(data.totals.card_fees)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700 text-base">{formatCurrency(data.totals.receita_liquida)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Audit rules footer */}
      {data?.audit_rules && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-xs text-slate-600 space-y-1" data-testid="reconc-audit-rules">
          <p className="font-semibold text-slate-700 mb-1">Regras da fonte única ({data.source}):</p>
          <p>• <strong>Frete:</strong> {data.audit_rules.delivery_fee}</p>
          <p>• <strong>Taxa Marketplace:</strong> {data.audit_rules.marketplace_fee}</p>
          <p>• <strong>Taxa Cartão:</strong> {data.audit_rules.card_fee}</p>
          <p>• <strong>Keeta:</strong> {data.audit_rules.keeta_fallback}</p>
        </div>
      )}
    </div>
  );
}

// ============ DRE TAB (with Drilldown) ============
function DRETab() {
  const [dre, setDre] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [useLedger, setUseLedger] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLine, setDrilldownLine] = useState(null);

  const loadDRE = useCallback(async () => {
    setLoading(true);
    try {
      if (useLedger) {
        const end = new Date().toISOString();
        const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
        const res = await finance.ledger.dre({ start, end });
        setDre(res.data);
      } else {
        const res = await finance.dre(days);
        setDre(res.data);
      }
    } catch (err) {
      toast.error('Erro ao carregar DRE');
    } finally {
      setLoading(false);
    }
  }, [days, useLedger]);

  useEffect(() => { loadDRE(); }, [loadDRE]);

  const openDrilldown = async (line) => {
    setDrilldownLine(line);
    try {
      const end = new Date().toISOString();
      const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      const res = await finance.ledger.drilldown({ line, start, end });
      setDrilldown(res.data);
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const closeDrilldown = () => {
    setDrilldown(null);
    setDrilldownLine(null);
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Carregando...</div>;
  if (!dre) return null;

  const rows = [
    { label: 'Receita Bruta', value: dre.receita_bruta, bold: true, drilldownKey: 'RECEITA_BRUTA' },
    { label: '(-) Deducoes', value: -dre.deducoes, indent: true, subItems: [
      { label: 'Taxas de Cartao', value: dre.deducoes_detail?.taxas || 0, drilldownKey: 'TAXAS' },
      { label: 'Estornos', value: dre.deducoes_detail?.estornos || 0, drilldownKey: 'ESTORNOS' },
      { label: 'Impostos', value: dre.deducoes_detail?.impostos || 0, drilldownKey: 'IMPOSTOS' },
    ]},
    { label: '= Receita Liquida', value: dre.receita_liquida, bold: true, highlight: true },
    { label: '(-) CMV (Custo da Mercadoria Vendida)', value: -dre.cmv, indent: true },
    { label: '= Lucro Bruto', value: dre.lucro_bruto, bold: true, highlight: true },
    { label: '(-) Custos Variaveis Operacionais', value: -dre.despesas_variaveis, indent: true, drilldownKey: 'DESPESAS' },
    { label: '= Margem de Contribuicao', value: dre.margem_contribuicao, bold: true },
    { label: '(-) Despesas Fixas', value: -dre.despesas_fixas, indent: true },
    { label: '= EBITDA', value: dre.ebitda, bold: true, highlight: true },
    { label: '(+/-) Resultado Financeiro', value: dre.resultado_financeiro, indent: true },
    { label: '= Lucro Liquido', value: dre.lucro_liquido, bold: true, highlight: true, final: true },
  ];

  return (
    <div className="space-y-4" data-testid="dre-tab">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-slate-600">Periodo:</span>
        {[7, 15, 30, 60, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`dre-period-${d}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${days === d ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
            {d} dias
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-sm text-slate-600 flex items-center gap-2">
            <input type="checkbox" checked={useLedger} onChange={e => setUseLedger(e.target.checked)}
              className="rounded border-slate-300" data-testid="dre-use-ledger" />
            Usar Ledger (v2)
          </label>
        </div>
      </div>

      {/* DRE Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-bold text-slate-800 text-lg">DRE - Demonstrativo de Resultado</h3>
          <p className="text-xs text-slate-500">Periodo: {formatDate(dre.period_start)} a {formatDate(dre.period_end)}</p>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row, i) => (
              <React.Fragment key={i}>
                <tr className={`border-b border-slate-100 ${row.highlight ? 'bg-slate-50' : ''} ${row.final ? 'bg-emerald-50' : ''}`}
                  data-testid={`dre-row-${i}`}>
                  <td className={`px-5 py-3 ${row.indent ? 'pl-10' : ''} ${row.bold ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                    {row.drilldownKey && useLedger ? (
                      <button onClick={() => openDrilldown(row.drilldownKey)}
                        className="hover:underline hover:text-blue-600 flex items-center gap-1" data-testid={`dre-drilldown-${row.drilldownKey}`}>
                        {row.label}
                        <FileText size={14} className="text-blue-500" />
                      </button>
                    ) : row.label}
                  </td>
                  <td className={`px-5 py-3 text-right font-mono ${row.bold ? 'font-semibold' : ''} ${row.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(row.value))}
                    {row.value < 0 && <span className="text-red-400 ml-1">(D)</span>}
                  </td>
                </tr>
                {/* Sub-items (deducoes detail) */}
                {row.subItems && row.subItems.map((sub, si) => (
                  <tr key={`sub-${i}-${si}`} className="border-b border-slate-50 bg-slate-25">
                    <td className="pl-14 pr-5 py-2 text-slate-500 text-xs">
                      {sub.drilldownKey && useLedger ? (
                        <button onClick={() => openDrilldown(sub.drilldownKey)}
                          className="hover:underline hover:text-blue-600 flex items-center gap-1" data-testid={`dre-drilldown-${sub.drilldownKey}`}>
                          {sub.label}
                          <FileText size={12} className="text-blue-400" />
                        </button>
                      ) : sub.label}
                    </td>
                    <td className="px-5 py-2 text-right font-mono text-xs text-slate-500">
                      {formatCurrency(sub.value)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drilldown Modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeDrilldown}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-800">Detalhes: {drilldownLine}</h3>
                <p className="text-xs text-slate-500">{drilldown.count} lancamentos • Total: {formatCurrency(drilldown.total)}</p>
              </div>
              <button onClick={closeDrilldown} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Data</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Tipo</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Conta</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Pedido</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {drilldown.items?.map((item, idx) => (
                    <tr key={item.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500">{formatDate(item.date)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{item.entryType}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{item.accountName}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {item.orderId ? item.orderId.slice(0, 8) : '-'}
                        {item.channel && <span className="ml-1 text-xs text-slate-400">({item.channel})</span>}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${item.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))}
                  {(!drilldown.items || drilldown.items.length === 0) && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nenhum lancamento encontrado</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MAIN PAGE ============
export function FinancePage() {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="finance-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Financeiro
          </h1>
          <p className="text-slate-600">Gerencie contas, pagamentos e receitas</p>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto" data-testid="finance-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`finance-tab-${tab.id}`}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'payables' && <PayablesTab />}
        {activeTab === 'receivables' && <ReceivablesTab />}
        {activeTab === 'accounts' && <AccountsTab />}
        {activeTab === 'transactions' && <TransactionsTab />}
        {activeTab === 'reconciliacao' && <ReconciliacaoTab />}
        {activeTab === 'dre' && <DRETab />}
      </div>
    </MainLayout>
  );
}
